// Cost review uses booked report lines. A high share of revenue is a review signal,
// not an estimated saving or a fixed/variable accounting classification.
const PlummeAiAdvisorCostInsights = (() => {
  const amountOf = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? Math.max(0, amount) : 0;
  };

  const build = ({ costLines = [], income = 0, fixedCostBasis = null } = {}) => {
    const fixedAccounts = Array.isArray(fixedCostBasis?.accounts)
      ? fixedCostBasis.accounts.filter((account) => amountOf(account.monthly) > 0).slice(0, 3)
      : [];
    const fixedCodes = new Set((fixedCostBasis?.accounts || []).map((account) => String(account.code)));
    const revenue = amountOf(income);
    const otherAccounts = (Array.isArray(costLines) ? costLines : [])
      .map((line) => ({ code: String(line.nr || '').trim(), name: String(line.name || line.nr || ''), amount: amountOf(line.amount) }))
      .filter((line) => line.amount > 0 && !fixedCodes.has(line.code))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 3)
      .map((line) => ({ ...line, revenueSharePct: revenue > 0 ? Math.round(line.amount / revenue * 1000) / 10 : null }));
    return { fixedAccounts, fixedMonths: fixedCostBasis?.months || 0, otherAccounts };
  };

  const buildAnalysis = ({ costLines = [], previousCostLines = [] } = {}) => {
    const previous = new Map((Array.isArray(previousCostLines) ? previousCostLines : [])
      .filter((line) => /^[4-8]\d{3,4}$/.test(String(line.nr || '')) && amountOf(line.amount) > 0)
      .map((line) => [String(line.nr), amountOf(line.amount)]));
    const accounts = (Array.isArray(costLines) ? costLines : [])
      .filter((line) => /^[4-8]\d{3,4}$/.test(String(line.nr || '')) && amountOf(line.amount) > 0)
      .sort((a, b) => amountOf(b.amount) - amountOf(a.amount))
      .slice(0, 5);
    const max = Math.max(1, ...accounts.map((line) => amountOf(line.amount)));
    return accounts.map((line) => {
      const amount = amountOf(line.amount);
      const prior = previous.get(String(line.nr));
      const change = prior == null ? null : Math.round((amount - prior) / prior * 100);
      return { code: String(line.nr), name: String(line.name || line.nr), amount,
        pct: amount / max, chg: change, increase: prior == null ? null : Math.round(amount - prior),
        flag: change != null && change > 25 };
    });
  };

  const buildObservations = (accounts = []) => accounts
    .filter((account) => account.flag && account.increase > 0)
    .sort((a, b) => b.increase - a.increase)
    .slice(0, 3)
    .map((account) => ({ key: 'whyCostUp', vars: { name: `${account.code} · ${account.name}`, pct: account.chg },
      amount: account.increase, tone: 'amber' }));

  const buildOpportunities = ({ moves = [], recommendations = [], liquidityMoves = [], salesModel = null } = {}) => {
    const items = [];
    const add = (item) => items.push(item);
    const recommendationsById = new Map((Array.isArray(recommendations) ? recommendations : []).map((rec) => [rec.id, rec]));
    const fromRecommendation = (id, priority, noteKey, valueTagKey) => {
      const rec = recommendationsById.get(id);
      if (!rec) return;
      add({ id, priority, title: rec.text, value: rec.impact, noteKey, valueTagKey, icon: rec.icon, rec });
    };
    fromRecommendation('unbilledProjectTime', 100, 'adv.cost.unbilledNote', 'adv.cost.invoiceBasis');
    fromRecommendation('draftInvoiceValue', 94, 'adv.cost.draftNote', 'adv.cost.draftValue');
    const accepted = (Array.isArray(liquidityMoves) ? liquidityMoves : []).find((move) => move.key === 'moveAcceptedOffer');
    if (accepted) add({ ...accepted, id: 'acceptedOffers', priority: 92, noteKey: 'adv.cost.acceptedNote' });
    (Array.isArray(moves) ? moves : []).filter((move) => move.key === 'moveReviewCost')
      .forEach((move, index) => add({ ...move, id: `costIncrease-${index}`, priority: 80 - index }));
    fromRecommendation('duplicatePurchase', 78, 'adv.cost.duplicateNote', 'adv.cost.possibleCount');
    fromRecommendation('supplierNegotiation', 70, 'adv.cost.supplierNote', 'adv.cost.purchaseShare');
    const discount = Number(salesModel?.discountAmount);
    if (Number.isFinite(discount) && discount > 0 && Number(salesModel?.kpis?.avgDiscountPct) >= 2) {
      add({ id: 'discounts', priority: 65, titleKey: 'adv.cost.discountTitle', value: discount,
        noteKey: 'adv.cost.discountNote', valueTagKey: 'adv.cost.givenDiscount', icon: 'percent', tab: 'salg' });
    }
    const vouchers = (Array.isArray(moves) ? moves : []).find((move) => move.key === 'moveFixVouchers');
    if (vouchers) add({ ...vouchers, id: 'missingVouchers', priority: 40, noteKey: 'adv.cost.voucherNote' });
    return items.sort((a, b) => b.priority - a.priority);
  };

  return { build, buildAnalysis, buildObservations, buildOpportunities };
})();
Object.assign(globalThis, { PlummeAiAdvisorCostInsights });
